import { useState } from "react";

import {
  useRubricVersions,
  useRubricVersion,
  useCreateRubricVersion,
  useActivateRubric,
  useAddRubricRule,
  useToggleRubricRule,
  useDeleteRubricRule,
  type RubricVersion,
  type RubricRule,
} from "@/hooks/useAdminBrain";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  BookOpen,
  Plus,
  CheckCircle2,
  Circle,
  Trash2,
  Loader2,
  ChevronRight,
  Zap,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

const RULE_CATEGORIES = ["hook", "cta", "visual", "copy", "structure", "emotional", "offer", "general"];

function RuleCard({ rule, versionId, isActive }: { rule: RubricRule; versionId: number; isActive: boolean }) {
  const toggleRule = useToggleRubricRule();
  const deleteRule = useDeleteRubricRule();
  const { toast } = useToast();

  const handleToggle = async (enabled: boolean) => {
    try {
      await toggleRule.mutateAsync({ versionId, ruleId: rule.id, enabled });
    } catch {
      toast({ title: "Failed to toggle rule", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRule.mutateAsync({ versionId, ruleId: rule.id });
      toast({ title: "Rule removed" });
    } catch {
      toast({ title: "Failed to remove rule", variant: "destructive" });
    }
  };

  const catColors: Record<string, string> = {
    hook: "#8B5CF6", cta: "#14B8A6", visual: "#F97316", copy: "#3B82F6",
    structure: "#EC4899", emotional: "#F59E0B", offer: "#10B981", general: "#6B7280",
  };
  const catColor = catColors[rule.category] || "#6B7280";

  return (
    <div
      className={cn(
        "bg-white border rounded-lg p-4 transition-all",
        rule.enabled ? "border-[#DCE3ED]" : "border-[#DCE3ED] opacity-50"
      )}
      data-testid={`rule-card-${rule.id}`}
    >
      <div className="flex items-start gap-3">
        <Switch
          checked={rule.enabled}
          onCheckedChange={handleToggle}
          disabled={toggleRule.isPending}
          data-testid={`toggle-rule-${rule.id}`}
          className="mt-0.5 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
              style={{ backgroundColor: `${catColor}20`, color: catColor }}
            >
              {rule.category}
            </span>
            <span className="text-[10px] text-[#526074]">Weight: {rule.weight.toFixed(1)}x</span>
            {rule.source && (
              <span className="text-[10px] text-[#526074] bg-white px-1.5 py-0.5 rounded">{rule.source}</span>
            )}
          </div>
          <p className="text-sm text-[#334155] leading-relaxed">{rule.rule_text}</p>
        </div>
        {isActive && (
          <button
            onClick={handleDelete}
            className="p-1.5 rounded hover:bg-red-900/30 transition-colors flex-shrink-0"
            data-testid={`button-delete-rule-${rule.id}`}
          >
            <Trash2 className="h-3.5 w-3.5 text-red-700" />
          </button>
        )}
      </div>
    </div>
  );
}

function AddRuleModal({ open, onOpenChange, versionId }: { open: boolean; onOpenChange: (v: boolean) => void; versionId: number }) {
  const [ruleText, setRuleText] = useState("");
  const [weight, setWeight] = useState("1.0");
  const [category, setCategory] = useState("general");
  const [source, setSource] = useState("");
  const addRule = useAddRubricRule();
  const { toast } = useToast();

  const handleSave = async () => {
    if (!ruleText.trim()) return;
    try {
      await addRule.mutateAsync({
        versionId,
        rule: {
          rule_text: ruleText.trim(),
          weight: parseFloat(weight) || 1.0,
          category,
          source: source.trim() || "manual",
          enabled: true,
        },
      });
      setRuleText(""); setWeight("1.0"); setCategory("general"); setSource("");
      onOpenChange(false);
      toast({ title: "Rule added" });
    } catch {
      toast({ title: "Failed to add rule", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-theme bg-white border-[#DCE3ED] text-[#1A1A1A] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <Plus className="h-4 w-4 text-teal-700" />
            Add Rubric Rule
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#526074] mb-1 block">Rule</label>
            <Textarea
              value={ruleText}
              onChange={e => setRuleText(e.target.value)}
              placeholder="e.g., The hook should create curiosity or tension within the first 3 words"
              className="bg-white border-[#DCE3ED] text-[#1A1A1A] placeholder:text-[#526074] min-h-[90px]"
              data-testid="input-rule-text"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#526074] mb-1 block">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-white border border-[#DCE3ED] text-[#1A1A1A] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                data-testid="select-rule-category"
              >
                {RULE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#526074] mb-1 block">Weight</label>
              <Input
                type="number"
                min="0.1"
                max="3.0"
                step="0.1"
                value={weight}
                onChange={e => setWeight(e.target.value)}
                className="bg-white border-[#DCE3ED] text-[#1A1A1A] text-sm"
                data-testid="input-rule-weight"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-[#526074] mb-1 block">Source</label>
              <Input
                value={source}
                onChange={e => setSource(e.target.value)}
                placeholder="manual"
                className="bg-white border-[#DCE3ED] text-[#1A1A1A] placeholder:text-[#526074] text-sm"
                data-testid="input-rule-source"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="border-[#DCE3ED] text-[#334155] hover:text-[#1A1A1A] hover:bg-[#F1F5F9]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!ruleText.trim() || addRule.isPending}
              className="bg-[#1A1A1A] hover:bg-[#334155]"
              data-testid="button-save-rule"
            >
              {addRule.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Add Rule
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewVersionModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const createVersion = useCreateRubricVersion();
  const { toast } = useToast();

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      await createVersion.mutateAsync({ name: name.trim(), description: description.trim() || undefined });
      setName(""); setDescription("");
      onOpenChange(false);
      toast({ title: "New rubric version created" });
    } catch {
      toast({ title: "Failed to create version", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="admin-theme bg-white border-[#DCE3ED] text-[#1A1A1A] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
            <BookOpen className="h-4 w-4 text-teal-700" />
            New Rubric Version
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#526074] mb-1 block">Name</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., v2 DTC E-commerce"
              className="bg-white border-[#DCE3ED] text-[#1A1A1A] placeholder:text-[#526074]"
              data-testid="input-version-name"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-[#526074] mb-1 block">Description</label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional description..."
              className="bg-white border-[#DCE3ED] text-[#1A1A1A] placeholder:text-[#526074]"
              data-testid="input-version-description"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="border-[#DCE3ED] text-[#334155] hover:text-[#1A1A1A] hover:bg-[#F1F5F9]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!name.trim() || createVersion.isPending}
              className="bg-[#1A1A1A] hover:bg-[#334155]"
              data-testid="button-save-version"
            >
              {createVersion.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Create Version
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function RubricEditorPage() {
  const { data: versions, isLoading } = useRubricVersions();
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [addRuleOpen, setAddRuleOpen] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const activateRubric = useActivateRubric();
  const { toast } = useToast();

  const { data: selectedVersion } = useRubricVersion(selectedVersionId);

  const activeVersion = versions?.find(v => v.isActive);
  const displayVersion = selectedVersion ?? (activeVersion && versions?.find(v => v.id === activeVersion?.id));

  const handleActivate = async (id: number) => {
    try {
      await activateRubric.mutateAsync(id);
      toast({ title: "Rubric activated" });
    } catch {
      toast({ title: "Failed to activate rubric", variant: "destructive" });
    }
  };

  const currentVersion = displayVersion ?? (versions?.length ? (selectedVersionId ? versions.find(v => v.id === selectedVersionId) : versions[0]) : null);

  return (
    <>
    <div className="flex flex-col gap-5 h-full">
        <div className="flex-shrink-0 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-teal-700" />
              Rubric Editor
            </h1>
            <p className="text-sm text-[#526074] mt-0.5">Define and manage creative scoring rules</p>
          </div>
          <Button
            onClick={() => setNewVersionOpen(true)}
            className="bg-[#1A1A1A] hover:bg-[#334155] text-white"
            data-testid="button-new-version"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New Version
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
          <div className="w-full lg:w-60 flex-shrink-0 bg-white border border-[#DCE3ED] rounded-xl overflow-hidden flex flex-col">
            <div className="p-3 border-b border-[#DCE3ED]">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#526074]">Versions</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-[#526074]" />
                </div>
              ) : versions?.length === 0 ? (
                <p className="text-xs text-[#526074] text-center py-8">No rubric versions yet</p>
              ) : (
                versions?.map(v => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVersionId(v.id)}
                    className={cn(
                      "w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-center gap-2",
                      (selectedVersionId === v.id || (!selectedVersionId && v.isActive))
                        ? "bg-[#1A1A1A]/20 text-teal-700"
                        : "text-[#526074] hover:text-[#334155] hover:bg-[#F1F5F9]"
                    )}
                    data-testid={`version-item-${v.id}`}
                  >
                    {v.isActive ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-teal-700 flex-shrink-0" />
                    ) : (
                      <Circle className="h-3.5 w-3.5 text-[#526074] flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">{v.name}</p>
                      <p className="text-[10px] text-[#526074]">v{v.versionNumber} · {v.rules?.length ?? 0} rules</p>
                    </div>
                    <ChevronRight className="h-3 w-3 flex-shrink-0 opacity-50" />
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 bg-white border border-[#DCE3ED] rounded-xl overflow-hidden flex flex-col">
            {!currentVersion ? (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <div className="h-14 w-14 rounded-2xl bg-white border border-[#DCE3ED] flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="h-7 w-7 text-[#526074]" />
                  </div>
                  <p className="text-base font-medium text-[#526074]">No rubric generated yet</p>
                  <p className="text-xs text-[#526074] mt-2 max-w-xs mx-auto">Create your first rubric version to define how ad creatives are scored by the Prediction Brain.</p>
                  <Button onClick={() => setNewVersionOpen(true)} className="mt-4 bg-[#1A1A1A] hover:bg-[#334155] text-white" size="sm" data-testid="button-create-first-version">
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Create First Version
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-[#DCE3ED] flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-semibold text-[#1A1A1A]">{currentVersion.name}</h2>
                      <span className="text-xs text-[#526074]">v{currentVersion.versionNumber}</span>
                      {currentVersion.isActive && (
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-teal-500/20 text-teal-700 uppercase tracking-wide">
                          Active
                        </span>
                      )}
                    </div>
                    {currentVersion.description && (
                      <p className="text-xs text-[#526074] mt-0.5">{currentVersion.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!currentVersion.isActive && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleActivate(currentVersion.id)}
                        disabled={activateRubric.isPending}
                        className="border-teal-600 text-teal-700 hover:bg-teal-600/20"
                        data-testid="button-activate-version"
                      >
                        {activateRubric.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                        <Zap className="h-3.5 w-3.5 mr-1" />
                        Activate
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => setAddRuleOpen(true)}
                      className="bg-[#1A1A1A] hover:bg-[#334155]"
                      data-testid="button-add-rule"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add Rule
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {!currentVersion.rules?.length ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <AlertCircle className="h-8 w-8 text-[#526074] mb-3" />
                      <p className="text-sm text-[#526074]">No rules yet</p>
                      <p className="text-xs text-[#526074] mt-1">Add rules to define how creatives are scored</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {currentVersion.rules.map(rule => (
                        <RuleCard
                          key={rule.id}
                          rule={rule}
                          versionId={currentVersion.id}
                          isActive={!!currentVersion.isActive}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {currentVersion && (
        <AddRuleModal open={addRuleOpen} onOpenChange={setAddRuleOpen} versionId={currentVersion.id} />
      )}
      <NewVersionModal open={newVersionOpen} onOpenChange={setNewVersionOpen} />
    </>
  );
}
